import { Archive, Bell, X } from "lucide-react";
import * as React from "react";

import type { Notification } from "@/store/notification";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { SocketProvider } from "@/context/notifications";
import { cn, formatKebabCase, formatRelativeDate } from "@/lib/utils";
import useAuthStore from "@/store/auth";
import { useNotificationStore } from "@/store/notification";
import { Link } from "@tanstack/react-router";

export function NotificationPopover() {
	return (
		<SocketProvider>
			<NotificationConsumer />
		</SocketProvider>
	);
}

export function NotificationConsumer() {
	const user = useAuthStore(state => state.user);
	const markAsRead = useNotificationStore(state => state.markAsRead);
	const getUserNotifications = useNotificationStore(state => state.getUserNotifications);
	const getUnreadNotificationsCount = useNotificationStore(state => state.getUnreadNotificationsCount);

	const notifications = getUserNotifications(user?.id ?? "");

	const unRead = getUnreadNotificationsCount(user?.id ?? "0");
	const [open, setOpen] = React.useState(false);

	const groupedNotifications = React.useMemo(() => {
		return notifications.reduce(
			(groups, notification) => {
				const date = new Date(notification.timestamp).toDateString();
				if (!groups[date]) {
					groups[date] = [];
				}
				groups[date].push(notification);
				return groups;
			},
			{} as Record<string, Notification[]>
		);
	}, [notifications]);

	const sortDates = (a: string | number, b: string | number) => {
		return new Date(b).getTime() - new Date(a).getTime();
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					size="icon"
					className="relative ml-auto"
				>
					<Bell className="h-4 w-4" />
					<Badge
						variant="secondary"
						className={cn("absolute top-2 right-2 size-2 bg-red-500 text-red-500 rounded-full p-0 text-xs", { hidden: !unRead })}
					>
					</Badge>
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-[380px] p-0">
				<div className="flex items-center justify-between p-4">
					<div className="flex items-center gap-2">
						<h4 className="font-semibold text-sm">Notification</h4>
						<Badge variant="secondary" className={cn({ hidden: !unRead })}>{unRead}</Badge>
					</div>
					<div className="flex items-center gap-2">

						<Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setOpen(false)}>
							<X className="h-4 w-4" />
							<span className="sr-only">Close</span>
						</Button>
					</div>
				</div>
				<Separator />
				<ScrollArea className="h-[450px]">
					<div className="flex flex-col gap-4 p-4">
						{Object.entries(groupedNotifications)
							.sort(([date_a], [date_b]) => sortDates(date_a, date_b))
							.map(([date, items]) => (
								<div key={date} className="space-y-4">
									<h5 className="text-xs font-medium text-muted-foreground px-2">
										{date}
									</h5>
									{items.sort((a, b) => sortDates(a.timestamp, b.timestamp)).map(notification => (
										<div
											key={crypto.randomUUID()}
											className={cn("rounded-lg bg-hover-secondary p-4 opacity-90 border border-primary hover:opacity-80 cursor-pointer transition-colors", {
												"bg-muted/60  border-black/10": notification.read
											})}
											onClick={() => {
												markAsRead(user?.id ?? "", notification.timestamp);
												setOpen(false);
											}}
										>
											<Link
												to={user?.userType === "BUYER" ? "/buyer/orders/$orderid" : "/vendor/orders/$orderid"}
												params={{ orderid: notification.data.orderId }}
											>
												<h6 className="font-medium mb-1 capitalize">{formatKebabCase(notification.data.notificationType)}</h6>
												<p className="text-sm text-muted-foreground mb-2">{notification.notification.text}</p>
												<p className="text-xs text-muted-foreground">{formatRelativeDate(notification.timestamp)}</p>
											</Link>
										</div>
									))}
								</div>
							))}
					</div>
				</ScrollArea>
				<Separator />
				<div className="grid grid-cols-2 gap-4 p-4">
					<Button variant="outline" className="w-full">
						<Archive className="mr-2 h-4 w-4" />
						Archive all
					</Button>
					<Button variant="outline" className="w-full">
						Mark all as read
					</Button>
				</div>
			</PopoverContent>
		</Popover>
	);
}
